import React, { useMemo } from "react";

import { Sparkles, TriangleAlert } from "lucide-react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";

import { WidgetCard } from "./StatWidgets";

// AI band × outcome — "is our judgment sound?". A flat, single-ring pie
// (not a nested donut) with a side legend, matching the classic
// "solid slices + legend list" look. Each slice is one band×outcome
// combination (e.g. "High · Accepted"); slices from the same band share a
// hue family (darker = accepted, mid = rejected, lightest = pending) so the
// three bands are still visually grouped even flattened into one ring.

const BAND_META = {
    high: { label: "High", hue: 199, sat: 42 },   
    medium: { label: "Medium", hue: 30, sat: 58 }, 
    low: { label: "Low", hue: 148, sat: 26 },      
};

const SEG_LABEL = {
    accepted: "Accepted",
    rejected: "Rejected",
    pending: "In progress",
};

const SEG_LIGHTNESS = {
    accepted: 38,
    rejected: 52,
    pending: 70,
};

const hsl = (hue, sat, lightness) => `hsl(${hue}, ${sat}%, ${lightness}%)`;

const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
        <div className="rounded-md border border-border-primary bg-background-primary px-2.5 py-1.5 shadow-md text-[11px]">
            <div className="font-semibold text-text-primary">{d.tooltipLabel}</div>
            <div className="text-text-secondary">
                {d.value} of {d.grandTotal} ({Math.round((d.value / d.grandTotal) * 100)}%)
            </div>
        </div>
    );
};

const AiBandOutcome = ({ ai, loading }) => {
    const bands = ai?.bands ?? [];
    const hasData = bands.some((b) => b.total > 0);

    const grandTotal = useMemo(
        () => bands.reduce((sum, b) => sum + (b.total || 0), 0),
        [bands]
    );

    const flatData = useMemo(() => {
        const rows = [];
        bands
            .filter((b) => b.total > 0)
            .forEach((b) => {
                const meta = BAND_META[b.band] ?? { label: b.label, hue: 220, sat: 10 };
                ["accepted", "rejected", "pending"].forEach((k) => {
                    const v = b[k] || 0;
                    if (v > 0) {
                        rows.push({
                            key: `${b.band}-${k}`,
                            name: `${meta.label} · ${SEG_LABEL[k]}`,
                            value: v,
                            fill: hsl(meta.hue, meta.sat, SEG_LIGHTNESS[k]),
                            tooltipLabel: `${meta.label} confidence · ${SEG_LABEL[k]}`,
                            grandTotal,
                        });
                    }
                });
            });
        return rows;
    }, [bands, grandTotal]);

    const highBand = bands.find((b) => b.band === "high");
    const lowTrust = highBand?.acceptanceRate != null && highBand.acceptanceRate < 70;

    return (
        <WidgetCard
            icon={Sparkles}
            title="AI Recommendation vs Outcome"
            insight={ai?.insight}
            loading={loading}
            right={
                ai?.scoredApplications ? (
                    <span className="text-[11px] text-text-secondary">
                        {ai.scoredApplications} scored
                    </span>
                ) : null
            }
        >
            {loading ? (
                <div className="h-[300px] w-full rounded-md bg-background-tertiary animate-pulse" />
            ) : !hasData ? (
                <p className="text-[12px] text-text-secondary py-8 text-center">
                    No AI-scored candidates to analyze yet.
                </p>
            ) : (
                <>
                    <div className="flex items-center gap-4 w-full h-[260px]">
                        {/* Pie */}
                        <div className="flex-1 h-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                                    <Tooltip content={<CustomTooltip />} />
                                    <Pie
                                        data={flatData}
                                        dataKey="value"
                                        nameKey="name"
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={0}
                                        outerRadius="90%"
                                        startAngle={90}
                                        endAngle={-270}
                                        stroke="var(--background-primary, #fff)"
                                        strokeWidth={1.5}
                                        isAnimationActive={false}
                                    >
                                        {flatData.map((d) => (
                                            <Cell key={d.key} fill={d.fill} />
                                        ))}
                                    </Pie>
                                </PieChart>
                            </ResponsiveContainer>
                        </div>

                        <div className="flex flex-col gap-2 flex-shrink-0 max-w-[42%]">
                            {flatData.map((d) => (
                                <div key={d.key} className="flex items-center gap-2">
                                    <span
                                        className="w-3 h-3 rounded-sm flex-shrink-0"
                                        style={{ background: d.fill }}
                                    />
                                    <span className="text-[11px] text-text-primary leading-tight truncate">
                                        {d.name}
                                    </span>
                                    <span className="text-[11px] text-text-secondary flex-shrink-0">
                                        ({d.value})
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {highBand?.acceptanceRate != null ? (
                        <div className="flex items-center justify-center gap-1.5 text-[11px]">
                            <span className="text-text-secondary">High-confidence acceptance:</span>
                            <span className="font-semibold text-text-primary">
                                {Math.round(highBand.acceptanceRate)}%
                            </span>
                            {lowTrust ? (
                                <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-500/10 text-amber-500 border border-amber-500/20 px-1.5 py-[1px] text-[9px] font-semibold">
                                    <TriangleAlert size={9} />
                                    low trust
                                </span>
                            ) : null}
                        </div>
                    ) : null}
                </>
            )}
        </WidgetCard>
    );
};

export default AiBandOutcome;